/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useMemo } from 'react'
import { createTheme, alpha, ThemeProvider as MUIThemeProvider } from '@mui/material/styles'
import { getPalettePreset, normalizePaletteId, DEFAULT_PALETTE_ID } from '../lib/brandPalettes'

const ThemeContext = createContext({})

const PALETTE_STORAGE_KEY = 'superAdminPalette'

/**
 * MUI buttons use a circular ::after focus ring and a circular touch ripple on tap.
 * Both look wrong on wide controls; use a rectangular outline for keyboard focus only.
 */
function buttonBaseFocusOverrides(theme) {
  return {
    '& .MuiTouchRipple-root': {
      display: 'none',
    },
    '&::after': {
      display: 'none',
    },
    '&.Mui-focusVisible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    '&.MuiButton-containedPrimary.Mui-focusVisible': {
      outline: `2px solid ${theme.palette.primary.contrastText}`,
    },
    '&.MuiButton-containedSecondary.Mui-focusVisible': {
      outline: `2px solid ${theme.palette.secondary.contrastText}`,
    },
  }
}

/**
 * Build the app theme for a mode ('light' | 'dark'), optionally re-skinned with a brand
 * palette preset (see src/lib/brandPalettes.js). With no preset this is the default
 * theme used across all portals.
 */
export function buildAppTheme(mode, paletteId = DEFAULT_PALETTE_ID) {
  const preset = getPalettePreset(paletteId)
  const presetColors = preset ? preset[mode === 'dark' ? 'dark' : 'light'] : null

  // Light mode: primary .main is the AA-safe text/control color on white; .light keeps
  // the decorative brand tint. Defaults are the wisteria-family colors from the design
  // system; presets swap in another approved brand combination.
  const primaryMain = presetColors?.primaryMain || (mode === 'light' ? '#6B5A9E' : '#8E7CC3')
  const primaryLight = presetColors?.primaryLight || '#B4A3D9'
  const primaryDark = presetColors?.primaryDark || '#5a4a85'
  const primaryContrastText = presetColors?.primaryContrastText || '#FFFFFF'
  const backgroundDefault = mode === 'dark'
    ? '#1A202C' // Chalkboard dark
    : presetColors?.backgroundDefault || '#F5F3F7' // Lavender tint

  return createTheme({
    palette: {
      mode,
      // Brand kit (public/images/brand/README.md): Periwinkle #acb3d8, Chartreuse
      // #969646, Post-it #f9e375, Lavender #e1cee2, Wisteria #8e6ba5, Chalkboard #4c4d4d.
      primary: {
        main: primaryMain,
        light: primaryLight,
        dark: primaryDark,
        contrastText: primaryContrastText,
      },
      secondary: {
        main: '#4c4d4d', // Chalkboard (brand kit)
        light: '#6b6c6c',
        dark: '#3a3b3b',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#969646', // Chartreuse (brand kit)
        light: '#c4c489',
        dark: '#7d7d3b',
        // Explicit: without this, MUI would auto-flip to white text on the new hue
        // (3.1:1, below AA). Dark text keeps filled success chips/buttons readable.
        contrastText: '#1A202C',
      },
      warning: {
        main: '#f9e375', // Post-it (brand kit)
        light: '#fbeda0',
        dark: '#E5C94D',
      },
      error: {
        main: '#E53E3E',
        light: '#FC8181',
        dark: '#C53030',
      },
      info: {
        main: '#acb3d8', // Periwinkle (brand kit)
        light: '#c5cae6',
        dark: '#8f97c4',
      },
      background: {
        default: backgroundDefault,
        paper: mode === 'dark' ? '#2D3748' : '#FFFFFF',
      },
      text: {
        primary: mode === 'dark' ? '#f1f5f9' : '#2D3748', // Darkened chalkboard for AA body text
        // WCAG AA body text on white/paper (4.5:1); gray.500 was too light for labels/links.
        secondary: mode === 'dark' ? '#cbd5e1' : '#3D4A5C',
      },
      divider: mode === 'dark' ? '#4A5568' : '#E8DAEF', // Lavender tint
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: '3rem',
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontSize: '2.25rem',
        fontWeight: 600,
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h4: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
        fontWeight: 400,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.6,
        fontWeight: 400,
      },
      button: {
        textTransform: 'none',
        fontWeight: 500,
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: ({ theme: muiTheme }) => buttonBaseFocusOverrides(muiTheme),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: mode === 'dark'
              ? '0 1px 3px rgba(0, 0, 0, 0.3)'
              : '0 1px 3px rgba(0, 0, 0, 0.12)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              boxShadow: mode === 'dark'
                ? '0 4px 6px rgba(0, 0, 0, 0.4)'
                : '0 4px 6px rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-2px)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '10px 24px',
            fontWeight: 500,
            textTransform: 'none',
            transition: 'all 0.2s ease',
          },
          outlined: {
            borderWidth: 2,
            '&:hover': {
              borderWidth: 2,
            },
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            },
          },
          containedPrimary: {
            ...(mode === 'light'
              ? {
                  backgroundColor: primaryMain,
                  color: primaryContrastText,
                  '&:hover': { backgroundColor: primaryDark },
                  '&.Mui-disabled': {
                    backgroundColor: '#A0AEC0',
                    color: '#FFFFFF',
                  },
                }
              : {}),
          },
          outlinedPrimary: {
            ...(mode === 'light'
              ? {
                  color: primaryMain,
                  borderColor: primaryMain,
                  '&:hover': {
                    borderColor: primaryDark,
                    backgroundColor: alpha(primaryMain, 0.06),
                  },
                }
              : {}),
          },
          textPrimary: {
            ...(mode === 'light'
              ? {
                  color: primaryMain,
                  '&:hover': { backgroundColor: alpha(primaryMain, 0.06) },
                }
              : {}),
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            // MuiPaper's radius would round and clip full-height side panels.
            borderRadius: 0,
            top: 0,
            height: '100dvh',
            maxHeight: '100dvh',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: 12,
            '&.MuiDrawer-paper': {
              borderRadius: 0,
            },
          },
          elevation1: {
            boxShadow: mode === 'dark'
              ? '0 1px 3px rgba(0, 0, 0, 0.3)'
              : '0 1px 3px rgba(0, 0, 0, 0.12)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            fontWeight: 500,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            ...(mode === 'light'
              ? {
                  color: '#4A5568',
                  '&.Mui-focused': { color: primaryMain },
                }
              : {}),
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            ...(mode === 'light'
              ? {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#CBD5E0',
                  },
                }
              : {}),
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
            // Native date/time segments need a real width or they paint on top of each other.
            '&:has(input[type="date"]), &:has(input[type="time"]), &:has(input[type="datetime-local"])': {
              minWidth: '12.5rem',
              flex: '0 0 auto',
            },
          },
        },
      },
    },
  })
}

export const useThemeMode = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeProvider')
  }
  return context
}

export const ThemeProvider = ({ children }) => {
  // Default to light mode for all users
  // Users can still toggle to dark mode, and their preference will be saved
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode')
    // Default to light mode - only use dark if explicitly saved as 'dark'
    return savedMode === 'dark' ? 'dark' : 'light'
  })

  // Brand palette preset; applied only within SuperAdminThemeScope (super admin portal).
  const [superAdminPalette, setSuperAdminPaletteState] = useState(() =>
    normalizePaletteId(localStorage.getItem(PALETTE_STORAGE_KEY)),
  )

  // Save mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('themeMode', mode)
  }, [mode])

  useEffect(() => {
    localStorage.setItem(PALETTE_STORAGE_KEY, superAdminPalette)
  }, [superAdminPalette])

  const toggleMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'))
  }

  const setSuperAdminPalette = (paletteId) => {
    setSuperAdminPaletteState(normalizePaletteId(paletteId))
  }

  const theme = useMemo(() => buildAppTheme(mode), [mode])

  const value = useMemo(
    () => ({
      mode,
      toggleMode,
      superAdminPalette,
      setSuperAdminPalette,
    }),
    [mode, superAdminPalette]
  )

  return (
    <ThemeContext.Provider value={value}>
      <MUIThemeProvider theme={theme}>{children}</MUIThemeProvider>
    </ThemeContext.Provider>
  )
}
